import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { formatPrice, getCurrency } from './utils/currency';
import { getFlightComparisons, removeFlightComparison, clearFlightComparisons } from './supabaseClient';
import './FlightComparisonPage.css';

const FlightComparisonPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [compareFlights, setCompareFlights] = useState([]);
  const [currency, setCurrency] = useState(null);

  useEffect(() => {
    const loadComparisons = async () => {
      try {
        // Try to load from Supabase first (has saved prices)
        const flights = await getFlightComparisons();
        if (flights && flights.length > 0) {
          setCompareFlights(flights);
        } else {
          // Fallback to location state or localStorage
          const fallbackFlights = location.state?.compareFlights || (() => {
            try {
              const saved = localStorage.getItem('compareFlights');
              return saved ? JSON.parse(saved) : [];
            } catch {
              return [];
            }
          })();
          setCompareFlights(fallbackFlights);
        }
      } catch (error) {
        console.error('Error loading comparisons:', error);
        // Fallback to location state or localStorage
        const fallbackFlights = location.state?.compareFlights || (() => {
          try {
            const saved = localStorage.getItem('compareFlights');
            return saved ? JSON.parse(saved) : [];
          } catch {
            return [];
          }
        })();
        setCompareFlights(fallbackFlights);
      }

      // Initialize currency
      const detectedCurrency = getCurrency();
      setCurrency(detectedCurrency);
    };
    
    loadComparisons();
  }, [location.state]);

  const handleRemoveFlight = async (flightId) => {
    try {
      await removeFlightComparison(flightId);
      const updated = compareFlights.filter(f => (f.id || f.offer_id) !== flightId);
      setCompareFlights(updated);
      localStorage.setItem('compareFlights', JSON.stringify(updated));
      
      if (updated.length < 2) {
        navigate('/results');
      }
    } catch (error) {
      console.error('Error removing flight:', error);
      // Fallback to local only
      const updated = compareFlights.filter(f => (f.id || f.offer_id) !== flightId);
      setCompareFlights(updated);
      localStorage.setItem('compareFlights', JSON.stringify(updated));
      
      if (updated.length < 2) {
        navigate('/results');
      }
    }
  };

  const handleSelectFlight = (flight) => {
    // Ensure price is properly formatted before passing to details page
    const formattedFlight = {
      ...flight,
      // Ensure price is a number or formatted string, not an object
      total: flight.price?.total 
        ? parseFloat(flight.price.total) 
        : (flight.priceFormatted 
          ? parseFloat(flight.priceFormatted.replace(/[^\d.]/g, '')) 
          : (typeof flight.total === 'number' ? flight.total : 0)),
      // Keep price object for reference but ensure it's properly structured
      price: flight.price && typeof flight.price === 'object' 
        ? {
            total: typeof flight.price.total === 'number' ? flight.price.total : parseFloat(flight.price.total || 0),
            base: typeof flight.price.base === 'number' ? flight.price.base : parseFloat(flight.price.base || 0),
            taxes: typeof flight.price.taxes === 'number' ? flight.price.taxes : parseFloat(flight.price.taxes || 0),
            currency: flight.price.currency || 'NGN'
          }
        : (typeof flight.price === 'number' ? { total: flight.price, base: 0, taxes: 0, currency: 'NGN' } : { total: 0, base: 0, taxes: 0, currency: 'NGN' })
    };
    
    navigate('/details', { state: { flight: formattedFlight, searchData: location.state?.searchData } });
  };

  const handleClearAll = async () => {
    try {
      await clearFlightComparisons();
      setCompareFlights([]);
      localStorage.removeItem('compareFlights');
      navigate('/results');
    } catch (error) {
      console.error('Error clearing comparisons:', error);
      // Fallback to local only
      setCompareFlights([]);
      localStorage.removeItem('compareFlights');
      navigate('/results');
    }
  };

  if (compareFlights.length < 2) {
    return (
      <>
        <Header />
        <div className="comparison-empty">
          <div className="comparison-empty-content">
            <h1>Not Enough Flights to Compare</h1>
            <p>Please select at least 2 flights to compare.</p>
            <button onClick={() => navigate('/results')} className="back-to-results-button">
              Back to Search Results
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="comparison-page">
        <div className="page-container">
          <div className="comparison-header">
            <div className="comparison-header-top">
              <h1>Compare Flights</h1>
              <button onClick={handleClearAll} className="clear-all-button">
                Clear All
              </button>
            </div>
            <p className="comparison-subtitle">
              Compare {compareFlights.length} flights side by side
            </p>
          </div>

          <div className="comparison-table-container">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th className="comparison-label-col">Details</th>
                  {compareFlights.map((flight, index) => (
                    <th key={flight.id || flight.offer_id || index} className="comparison-flight-col">
                      <div className="comparison-flight-header">
                        <button
                          className="remove-flight-button"
                          onClick={() => handleRemoveFlight(flight.id || flight.offer_id)}
                          title="Remove from comparison"
                        >
                          ×
                        </button>
                        <div className="comparison-flight-airline">
                          <span className="comparison-airline-logo">{flight.airlineLogo || '✈️'}</span>
                          <span className="comparison-airline-name">{flight.airline || 'N/A'}</span>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Price Row */}
                <tr>
                  <td className="comparison-label">Price</td>
                  {compareFlights.map((flight, index) => {
                    // Safely extract price value
                    let priceValue = 0;
                    if (flight.price?.total) {
                      priceValue = typeof flight.price.total === 'number' 
                        ? flight.price.total 
                        : parseFloat(flight.price.total || 0);
                    } else if (flight.priceFormatted) {
                      priceValue = parseFloat(flight.priceFormatted.replace(/[^\d.]/g, '') || 0);
                    } else if (typeof flight.total === 'number') {
                      priceValue = flight.total;
                    } else if (typeof flight.price === 'number') {
                      priceValue = flight.price;
                    }
                    
                    return (
                      <td key={index} className="comparison-value">
                        <div className="comparison-price">
                          {formatPrice(priceValue)}
                        </div>
                      </td>
                    );
                  })}
                </tr>

                {/* Route Row */}
                <tr>
                  <td className="comparison-label">Route</td>
                  {compareFlights.map((flight, index) => (
                    <td key={index} className="comparison-value">
                      <div className="comparison-route">
                        {flight.departureAirport || flight.from || 'N/A'} → {flight.arrivalAirport || flight.to || 'N/A'}
                        {flight.isRoundTrip && (
                          <div className="comparison-return-route">
                            Return: {flight.returnFlight?.departureAirport || 'N/A'} → {flight.returnFlight?.arrivalAirport || 'N/A'}
                          </div>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Airline Row */}
                <tr>
                  <td className="comparison-label">Airline</td>
                  {compareFlights.map((flight, index) => (
                    <td key={index} className="comparison-value">
                      <div className="comparison-airline">
                        {flight.airline || 'N/A'}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Flight Duration Row */}
                <tr>
                  <td className="comparison-label">Duration</td>
                  {compareFlights.map((flight, index) => (
                    <td key={index} className="comparison-value">
                      <div className="comparison-duration">
                        <div className="comparison-duration-outbound">
                          Outbound: {flight.duration || 'N/A'}
                        </div>
                        {flight.isRoundTrip && flight.returnFlight && (
                          <div className="comparison-duration-return">
                            Return: {flight.returnFlight.duration || 'N/A'}
                          </div>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Stopovers Row */}
                <tr>
                  <td className="comparison-label">Stopovers</td>
                  {compareFlights.map((flight, index) => (
                    <td key={index} className="comparison-value">
                      <div className="comparison-stops">
                        <div className="comparison-stops-outbound">
                          Outbound: {flight.stops === 0 ? 'Non-stop' : `${flight.stops} Stop${flight.stops > 1 ? 's' : ''}`}
                        </div>
                        {flight.isRoundTrip && flight.returnFlight && (
                          <div className="comparison-stops-return">
                            Return: {flight.returnFlight.stops === 0 ? 'Non-stop' : `${flight.returnFlight.stops} Stop${flight.returnFlight.stops > 1 ? 's' : ''}`}
                          </div>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Departure Time Row */}
                <tr>
                  <td className="comparison-label">Departure Time</td>
                  {compareFlights.map((flight, index) => (
                    <td key={index} className="comparison-value">
                      <div className="comparison-time">
                        <div className="comparison-time-outbound">
                          Outbound: {flight.departureTime || 'N/A'}
                        </div>
                        {flight.isRoundTrip && flight.returnFlight && (
                          <div className="comparison-time-return">
                            Return: {flight.returnFlight.departureTime || 'N/A'}
                          </div>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Arrival Time Row */}
                <tr>
                  <td className="comparison-label">Arrival Time</td>
                  {compareFlights.map((flight, index) => (
                    <td key={index} className="comparison-value">
                      <div className="comparison-time">
                        <div className="comparison-time-outbound">
                          Outbound: {flight.arrivalTime || 'N/A'}
                        </div>
                        {flight.isRoundTrip && flight.returnFlight && (
                          <div className="comparison-time-return">
                            Return: {flight.returnFlight.arrivalTime || 'N/A'}
                          </div>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Amenities Row */}
                <tr>
                  <td className="comparison-label">Amenities</td>
                  {compareFlights.map((flight, index) => (
                    <td key={index} className="comparison-value">
                      <div className="comparison-amenities">
                        {flight.amenities && flight.amenities.length > 0 ? (
                          flight.amenities.map((amenity, idx) => (
                            <span key={idx} className="comparison-amenity-tag">{amenity}</span>
                          ))
                        ) : (
                          <span>N/A</span>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Actions Row */}
                <tr>
                  <td className="comparison-label">Actions</td>
                  {compareFlights.map((flight, index) => (
                    <td key={index} className="comparison-value">
                      <button
                        className="comparison-select-button"
                        onClick={() => handleSelectFlight(flight)}
                      >
                        Select Flight
                      </button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div className="comparison-footer">
            <button onClick={() => navigate('/results')} className="back-to-results-button">
              ← Back to Search Results
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FlightComparisonPage;
